import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { DatabaseSearchRegular } from '@fluentui/react-icons';
import {
  seedObligations,
  setObligationGeneration,
  useActiveObligations,
  type ObligationSeedReport,
} from '../../services/complianceApi';
import { useIsStateTaxAdmin, useStateTaxClients } from '../../services/stateTaxClientsApi';

/**
 * Admin-only obligation seeding panel (spec 2026-08-05 Part A, plan §Rollout).
 *
 * Drives the two backend endpoints that had no UI at all:
 *   POST compliance/obligations/seed        { apply }            — dry run by default
 *   POST compliance/obligations/generation  { clientId?, enabled }
 *
 * The whole point is the ORDER. Seeding 72 obligations is harmless; switching
 * generation on immediately afterwards is what mints tasks, and doing it fleet-wide
 * before checking one client is how the supervision activation nearly minted 229
 * duplicates. So the three steps are rendered as a stepper with explicit locks:
 *
 *   1. Dry run   — always available, writes nothing, the entry point.
 *   2. Apply     — locked until a dry run has been reviewed in THIS session, and
 *                  then only behind a confirm that states the exact row count.
 *   3. Switch on — locked until the obligations actually exist (apply completed in
 *                  this session, or a dry run reported nothing left to seed, which
 *                  is what a previously-applied fleet looks like). Fleet-wide needs
 *                  a second, stronger acknowledgement. Turning generation OFF is the
 *                  rollback and is therefore never locked.
 */

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },
  step: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
  },
  stepCurrent: {
    border: `1px solid ${tokens.colorBrandStroke1}`,
    boxShadow: tokens.shadow4,
  },
  stepLocked: {
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground3,
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  stepTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
  },
  stepNumber: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'auto max-content',
    columnGap: tokens.spacingHorizontalXL,
    rowGap: tokens.spacingVerticalXS,
    margin: 0,
  },
  statValue: {
    margin: 0,
    fontWeight: tokens.fontWeightSemibold,
    textAlign: 'right',
  },
  rejectionList: {
    margin: 0,
    paddingLeft: tokens.spacingHorizontalXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    maxHeight: '220px',
    overflowY: 'auto',
  },
  scopeScroll: {
    maxHeight: '220px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusSmall,
    padding: tokens.spacingVerticalS,
  },
  dialogStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

type StepState = 'done' | 'current' | 'locked';

/** Fleet-wide is the absence of a client id; '' is the radio value that carries it. */
const FLEET = '';

/**
 * The marker the backend stamps on every obligation seeding writes
 * (`DataverseObligationSeedMapper.SeedCreatedBy`). `SetGeneration` with no clientId
 * sweeps only obligations carrying it, so it is also the only honest basis for the
 * fleet-wide blast radius shown here. Obligations without it predate seeding, have
 * been generating tasks for months, and the sweep deliberately leaves them alone.
 */
const SEED_CREATED_BY = 'dataverse-seed';

/**
 * Turn an unknown rejection into something a human can act on. 403 is the one that
 * actually happens (the operator's email is not on STATETAX_SALESTAX_ADMINS), and a
 * bare "failed" for it sends people looking for a bug instead of a roster entry.
 * Duck-typed on `status` rather than `instanceof ApiError` so a mocked API module in
 * tests — and any future error class — still routes correctly.
 */
function useErrorMessage() {
  const { t } = useTranslation();
  return (err: unknown): string => {
    const status = (err as { status?: number } | null | undefined)?.status;
    if (status === 403) return t('obligationSeeding.errors.forbidden');
    const message = err instanceof Error && err.message ? err.message : '';
    return message
      ? t('obligationSeeding.errors.failed', { error: message })
      : t('obligationSeeding.errors.unknown');
  };
}

export default function ObligationSeedingPanel() {
  const styles = useStyles();
  const { t } = useTranslation();
  const toMessage = useErrorMessage();

  // Admin gate — the SERVER's answer (StaffAdminGate, via /state-tax/users/me/admin-status), the
  // same check the seed endpoints enforce. Not a match against the staff roster: that lists
  // preparers and can be derived from ownership (PR #36). The server is the real boundary; this
  // just stops non-admins seeing a panel they can only get 403s from.
  const { data: isAdmin = false } = useIsStateTaxAdmin();

  // ── Step state ────────────────────────────────────────────────────────────────
  const [dryReport, setDryReport] = useState<ObligationSeedReport | null>(null);
  const [applyReport, setApplyReport] = useState<ObligationSeedReport | null>(null);
  const [dryPending, setDryPending] = useState(false);
  const [applyPending, setApplyPending] = useState(false);
  const [genPending, setGenPending] = useState(false);
  const [dryError, setDryError] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [genError, setGenError] = useState<string | null>(null);
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);

  // Step 3
  const [scope, setScope] = useState<string>(FLEET);
  const [clientSearch, setClientSearch] = useState('');
  const [pendingEnable, setPendingEnable] = useState<boolean | null>(null);
  const [fleetAck, setFleetAck] = useState(false);
  const [genResult, setGenResult] = useState<{ changed: number; enabled: boolean } | null>(null);

  const { data: clients } = useStateTaxClients();
  const { data: obligations } = useActiveObligations();

  // Whole-fleet state — every active obligation, seeded or not. Only the "right now"
  // line uses this: it reports the state of the fleet, not the reach of any button.
  const fleet = useMemo(() => {
    const rows = obligations ?? [];
    const clientIds = new Set(rows.map((o) => o.clientId));
    // `generationEnabled` is absent on documents written before the flag existed;
    // the backend defaults those to true, so absent counts as on.
    const on = rows.filter((o) => o.generationEnabled !== false).length;
    return { obligations: rows.length, clients: clientIds.size, on, off: rows.length - on };
  }, [obligations]);

  // Fleet-wide blast radius. This must mirror what SetGeneration actually walks with
  // no clientId — obligations stamped by seeding, and nothing else. Counting the whole
  // fleet here would overstate both buttons and, on the rollback, tell the operator the
  // opposite of what happens: legacy obligations keep generating.
  const seeded = useMemo(() => {
    const rows = (obligations ?? []).filter((o) => o.createdBy === SEED_CREATED_BY);
    return { obligations: rows.length, clients: new Set(rows.map((o) => o.clientId)).size };
  }, [obligations]);

  const clientOptions = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    const all = clients ?? [];
    const matched = q ? all.filter((c) => c.accountName.toLowerCase().includes(q)) : all;
    return [...matched].sort((a, b) => a.accountName.localeCompare(b.accountName));
  }, [clients, clientSearch]);

  const selectedClient = useMemo(
    () => (clients ?? []).find((c) => c.stateTaxClientId === scope),
    [clients, scope],
  );

  // Nothing left to write: either this session applied, or the dry run says the
  // fleet is already seeded. Both mean step 3 is the real next action.
  const nothingLeftToSeed = dryReport !== null && dryReport.seeded === 0;
  const seedingComplete = applyReport !== null || nothingLeftToSeed;

  const step1: StepState = dryReport === null ? 'current' : 'done';
  const step2: StepState =
    dryReport === null ? 'locked' : applyReport !== null || nothingLeftToSeed ? 'done' : 'current';
  const step3: StepState = seedingComplete ? 'current' : 'locked';

  async function runDryRun() {
    setDryError(null);
    setDryPending(true);
    try {
      const report = await seedObligations(false);
      setDryReport(report);
      // A fresh plan invalidates the previous apply's summary — otherwise step 2 keeps
      // claiming "done" against numbers that no longer describe the current fleet.
      setApplyReport(null);
    } catch (err) {
      setDryError(toMessage(err));
    } finally {
      setDryPending(false);
    }
  }

  async function runApply() {
    setApplyError(null);
    setApplyPending(true);
    try {
      const report = await seedObligations(true);
      setApplyReport(report);
      setApplyConfirmOpen(false);
    } catch (err) {
      setApplyError(toMessage(err));
    } finally {
      setApplyPending(false);
    }
  }

  async function runGeneration(enabled: boolean) {
    setGenError(null);
    setGenPending(true);
    try {
      const result = await setObligationGeneration(enabled, scope || undefined);
      setGenResult(result);
      setPendingEnable(null);
      setFleetAck(false);
    } catch (err) {
      setGenError(toMessage(err));
    } finally {
      setGenPending(false);
    }
  }

  if (!isAdmin) return null;

  const isFleetScope = scope === FLEET;
  const scopeName = selectedClient?.accountName ?? '';

  const stepClass = (state: StepState) =>
    [
      styles.step,
      state === 'current' ? styles.stepCurrent : '',
      state === 'locked' ? styles.stepLocked : '',
    ]
      .filter(Boolean)
      .join(' ');

  const stepBadge = (state: StepState) => (
    <Badge
      appearance={state === 'current' ? 'filled' : 'tint'}
      color={state === 'done' ? 'success' : state === 'current' ? 'brand' : 'informative'}
    >
      {t(`obligationSeeding.status.${state}`)}
    </Badge>
  );

  return (
    <section className={styles.section} data-testid="obligation-seeding-panel">
      <div className={styles.sectionTitle}>
        <DatabaseSearchRegular />
        {t('obligationSeeding.title')}
      </div>
      <span className={styles.subtitle}>{t('obligationSeeding.subtitle')}</span>

      {/* ── Step 1 — dry run ─────────────────────────────────────────────────── */}
      <div className={stepClass(step1)}>
        <div className={styles.stepHeader}>
          <span className={styles.stepNumber}>{t('obligationSeeding.stepLabel', { n: 1 })}</span>
          <span className={styles.stepTitle}>{t('obligationSeeding.step1.title')}</span>
          {stepBadge(step1)}
        </div>
        <Text className={styles.subtitle}>{t('obligationSeeding.step1.description')}</Text>

        <div className={styles.actions}>
          <Button
            appearance="primary"
            disabled={dryPending}
            onClick={() => { void runDryRun(); }}
          >
            {t('obligationSeeding.step1.run')}
          </Button>
          {dryPending && <Spinner size="tiny" label={t('obligationSeeding.step1.running')} />}
        </div>

        {dryError && (
          <MessageBar intent="error">
            <MessageBarBody>{dryError}</MessageBarBody>
          </MessageBar>
        )}

        {dryReport && (
          <>
            <MessageBar intent="info">
              <MessageBarBody>{t('obligationSeeding.step1.nothingWritten')}</MessageBarBody>
            </MessageBar>
            <dl className={styles.stats}>
              <dt>{t('obligationSeeding.step1.examined')}</dt>
              <dd className={styles.statValue}>{dryReport.examined}</dd>
              <dt>{t('obligationSeeding.step1.wouldSeed')}</dt>
              <dd className={styles.statValue}>{dryReport.seeded}</dd>
              <dt>{t('obligationSeeding.step1.alreadyPresent')}</dt>
              <dd className={styles.statValue}>{dryReport.skippedExisting}</dd>
              <dt>{t('obligationSeeding.step1.rejected')}</dt>
              <dd className={styles.statValue}>{dryReport.rejections.length}</dd>
            </dl>

            {/* Rejections are listed in full, never summarised — a silently dropped
                obligation is a client who quietly never gets a task again. */}
            {dryReport.rejections.length > 0 ? (
              <MessageBar intent="warning">
                <MessageBarBody>
                  <div>{t('obligationSeeding.step1.rejectionsTitle')}</div>
                  <ul className={styles.rejectionList} data-testid="seed-rejections">
                    {dryReport.rejections.map((r, i) => (
                      <li key={`${i}-${r}`}>{r}</li>
                    ))}
                  </ul>
                </MessageBarBody>
              </MessageBar>
            ) : (
              <span className={styles.hint}>{t('obligationSeeding.step1.noRejections')}</span>
            )}
          </>
        )}
      </div>

      {/* ── Step 2 — apply ───────────────────────────────────────────────────── */}
      <div className={stepClass(step2)}>
        <div className={styles.stepHeader}>
          <span className={styles.stepNumber}>{t('obligationSeeding.stepLabel', { n: 2 })}</span>
          <span className={styles.stepTitle}>{t('obligationSeeding.step2.title')}</span>
          {stepBadge(step2)}
        </div>
        <Text className={styles.subtitle}>{t('obligationSeeding.step2.description')}</Text>

        {step2 === 'locked' && (
          <span className={styles.hint}>{t('obligationSeeding.step2.lockedHint')}</span>
        )}
        {dryReport !== null && nothingLeftToSeed && applyReport === null && (
          <MessageBar intent="success">
            <MessageBarBody>{t('obligationSeeding.step2.nothingToApply')}</MessageBarBody>
          </MessageBar>
        )}

        <div className={styles.actions}>
          <Button
            appearance="primary"
            disabled={dryReport === null || dryReport.seeded === 0 || applyPending}
            onClick={() => { setApplyError(null); setApplyConfirmOpen(true); }}
          >
            {t('obligationSeeding.step2.button')}
          </Button>
        </div>

        {applyError && !applyConfirmOpen && (
          <MessageBar intent="error">
            <MessageBarBody>{applyError}</MessageBarBody>
          </MessageBar>
        )}

        {applyReport && (
          <>
            <dl className={styles.stats}>
              <dt>{t('obligationSeeding.step2.seededCount')}</dt>
              <dd className={styles.statValue}>{applyReport.seeded}</dd>
              <dt>{t('obligationSeeding.step1.alreadyPresent')}</dt>
              <dd className={styles.statValue}>{applyReport.skippedExisting}</dd>
            </dl>
            {applyReport.rejections.length > 0 && (
              <MessageBar intent="warning">
                <MessageBarBody>
                  <div>{t('obligationSeeding.step1.rejectionsTitle')}</div>
                  <ul className={styles.rejectionList} data-testid="apply-rejections">
                    {applyReport.rejections.map((r, i) => (
                      <li key={`${i}-${r}`}>{r}</li>
                    ))}
                  </ul>
                </MessageBarBody>
              </MessageBar>
            )}
            {/* The whole design of the backend hangs on this sentence being true. */}
            <MessageBar intent="success">
              <MessageBarBody>{t('obligationSeeding.step2.inertNotice')}</MessageBarBody>
            </MessageBar>
          </>
        )}
      </div>

      {/* ── Step 3 — switch generation on ────────────────────────────────────── */}
      <div className={stepClass(step3)}>
        <div className={styles.stepHeader}>
          <span className={styles.stepNumber}>{t('obligationSeeding.stepLabel', { n: 3 })}</span>
          <span className={styles.stepTitle}>{t('obligationSeeding.step3.title')}</span>
          {stepBadge(step3)}
        </div>
        <Text className={styles.subtitle}>{t('obligationSeeding.step3.description')}</Text>

        {step3 === 'locked' && (
          <span className={styles.hint}>{t('obligationSeeding.step3.lockedHint')}</span>
        )}

        <span className={styles.hint} data-testid="generation-state">
          {t('obligationSeeding.step3.currentState', {
            on: fleet.on,
            off: fleet.off,
            clients: fleet.clients,
          })}
        </span>

        <Field label={t('obligationSeeding.step3.searchLabel')}>
          <Input
            value={clientSearch}
            placeholder={t('obligationSeeding.step3.searchPlaceholder')}
            onChange={(_e, d) => setClientSearch(d.value)}
          />
        </Field>

        {/* Search + RadioGroup rather than a Fluent Combobox: same pattern the tasks
            page client filter uses, and it keeps the whole picker out of a portal.

            The fleet option deliberately reaches only what seeding created. The backend
            also accepts `includeUnseeded: true` to reach obligations that predate
            seeding; there is no control for it on purpose. If one is ever wanted it
            belongs here as a third radio (a distinct scope, not a modifier of this one),
            with its own acknowledgement text, and setObligationGeneration() would need a
            third argument to carry the flag through. */}
        <div className={styles.scopeScroll}>
          <RadioGroup value={scope} onChange={(_e, d) => setScope(d.value)}>
            <Radio
              value={FLEET}
              label={t('obligationSeeding.step3.fleetOption', {
                obligations: seeded.obligations,
                clients: seeded.clients,
              })}
            />
            {clientOptions.map((c) => (
              <Radio key={c.stateTaxClientId} value={c.stateTaxClientId} label={c.accountName} />
            ))}
          </RadioGroup>
          {clientOptions.length === 0 && (
            <span className={styles.hint}>{t('obligationSeeding.step3.noClients')}</span>
          )}
        </div>

        <div className={styles.actions}>
          <Button
            appearance="primary"
            disabled={step3 === 'locked' || genPending}
            onClick={() => { setGenError(null); setFleetAck(false); setPendingEnable(true); }}
          >
            {t('obligationSeeding.step3.turnOn')}
          </Button>
          {/* Rollback is never locked — the point of a rollback is that you can reach
              it from a state you did not plan to be in. */}
          <Button
            appearance="secondary"
            disabled={genPending}
            onClick={() => { setGenError(null); setFleetAck(false); setPendingEnable(false); }}
          >
            {t('obligationSeeding.step3.turnOff')}
          </Button>
        </div>

        {genError && pendingEnable === null && (
          <MessageBar intent="error">
            <MessageBarBody>{genError}</MessageBarBody>
          </MessageBar>
        )}

        {genResult && (
          <MessageBar intent="success">
            <MessageBarBody>
              {t(
                genResult.enabled
                  ? 'obligationSeeding.step3.doneOn'
                  : 'obligationSeeding.step3.doneOff',
                { changed: genResult.changed },
              )}
            </MessageBarBody>
          </MessageBar>
        )}
      </div>

      {/* ── Confirm: apply ───────────────────────────────────────────────────── */}
      <Dialog
        open={applyConfirmOpen}
        onOpenChange={(_e, d) => { if (!d.open && !applyPending) setApplyConfirmOpen(false); }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {t('obligationSeeding.step2.confirmTitle', { n: dryReport?.seeded ?? 0 })}
            </DialogTitle>
            <DialogContent>
              <div className={styles.dialogStack}>
                <Text>
                  {t('obligationSeeding.step2.confirmBody', { n: dryReport?.seeded ?? 0 })}
                </Text>
                <MessageBar intent="info">
                  <MessageBarBody>{t('obligationSeeding.step2.confirmInert')}</MessageBarBody>
                </MessageBar>
                {applyError && (
                  <MessageBar intent="error">
                    <MessageBarBody>{applyError}</MessageBarBody>
                  </MessageBar>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                disabled={applyPending}
                onClick={() => setApplyConfirmOpen(false)}
              >
                {t('obligationSeeding.cancel')}
              </Button>
              <Button
                appearance="primary"
                disabled={applyPending}
                onClick={() => { void runApply(); }}
              >
                {t('obligationSeeding.step2.confirmButton')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* ── Confirm: generation on/off ───────────────────────────────────────── */}
      <Dialog
        open={pendingEnable !== null}
        onOpenChange={(_e, d) => { if (!d.open && !genPending) { setPendingEnable(null); setFleetAck(false); } }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              {t(
                pendingEnable
                  ? 'obligationSeeding.step3.confirmOnTitle'
                  : 'obligationSeeding.step3.confirmOffTitle',
              )}
            </DialogTitle>
            <DialogContent>
              <div className={styles.dialogStack}>
                <Text>
                  {pendingEnable
                    ? isFleetScope
                      ? t('obligationSeeding.step3.confirmOnFleet', {
                          obligations: seeded.obligations,
                          clients: seeded.clients,
                        })
                      : t('obligationSeeding.step3.confirmOnClient', { client: scopeName })
                    : isFleetScope
                      ? t('obligationSeeding.step3.confirmOffFleet', {
                          obligations: seeded.obligations,
                          clients: seeded.clients,
                        })
                      : t('obligationSeeding.step3.confirmOffClient', { client: scopeName })}
                </Text>

                {/* Fleet-wide switch-on is the one irreversible-feeling action here: the
                    nightly timer mints for everyone the next morning. A second, explicit
                    acknowledgement of the blast radius stands between it and a stray click. */}
                {pendingEnable && isFleetScope && (
                  <>
                    <MessageBar intent="warning">
                      <MessageBarBody>
                        {t('obligationSeeding.step3.fleetWarning', {
                          obligations: seeded.obligations,
                          clients: seeded.clients,
                        })}
                      </MessageBarBody>
                    </MessageBar>
                    <Checkbox
                      checked={fleetAck}
                      onChange={(_e, d) => setFleetAck(d.checked === true)}
                      label={t('obligationSeeding.step3.fleetAck', {
                        obligations: seeded.obligations,
                        clients: seeded.clients,
                      })}
                    />
                  </>
                )}

                {genError && (
                  <MessageBar intent="error">
                    <MessageBarBody>{genError}</MessageBarBody>
                  </MessageBar>
                )}
              </div>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                disabled={genPending}
                onClick={() => { setPendingEnable(null); setFleetAck(false); }}
              >
                {t('obligationSeeding.cancel')}
              </Button>
              <Button
                appearance="primary"
                disabled={genPending || (pendingEnable === true && isFleetScope && !fleetAck)}
                onClick={() => { void runGeneration(pendingEnable === true); }}
              >
                {t(
                  pendingEnable
                    ? 'obligationSeeding.step3.confirmOnButton'
                    : 'obligationSeeding.step3.confirmOffButton',
                )}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </section>
  );
}
